import React, { ReactElement } from 'react';
import Chance from 'chance';
import { TEASER_TYPE } from 'b2b-components/theme/components/CMS/Teaser';
import { isEmpty } from 'b2b-apps/shop/common/utils/empty/empty';
import { useSid } from 'b2b-apps/shop/context/hooks';
import { useTranslate } from 'b2b-apps/shop/i18n/hooks';
import { BannerSlideProps } from '../Component';
import { EnhancedComponentProps, EnhancedComponentType, InjectedProps, WrappedComponentType } from './';

const withContainer = (Component: WrappedComponentType): EnhancedComponentType => {
  const WithContainer = (props: EnhancedComponentProps): ReactElement => {
    const t = useTranslate();
    const sid = useSid();
    const {
      handleClick,
      dataAttributes,
      componentData,
      ...originalProps
    } = props;
    const bannerData = componentData.global__banner?.componentData;

    if (isEmpty(bannerData) || !Array.isArray(bannerData)) {
      return null;
    }

    const validBannerSlides = bannerData.reduce(
      (banners: Record<string, any>[], data: Record<string, any>) => !isEmpty(data) ? [...banners, data] : banners,
      [],
    );

    const hasRandomOrder = componentData.random_order;
    let shuffledBannerSlides = validBannerSlides;

    if (hasRandomOrder) {
      const chance = new Chance(sid);
      const slidesToShuffle = validBannerSlides.filter((slide: any) => !slide.sticky_banner);
      const shuffledSlides = chance.shuffle(slidesToShuffle);

      shuffledBannerSlides = validBannerSlides.reduce(
        (props: any[], slide: any) => {
          if (slide.sticky_banner) {
            return [...props, slide];
          }

          return [...props, shuffledSlides.shift()];
        },
        [],
      );
    }

    const text = {
      ariaCloseModal: t('Close video'),
      ariaLabel: t('Teaser video'),
    };

    const hasSlider = componentData.show_as_slider && bannerData.length > 1;
    const slidesProps = shuffledBannerSlides.map(
      (data: Record<string, any>): BannerSlideProps => {
        const slideProps: any = {};

        if (data.mpt_id) {
          slideProps.dataAttributes = {
            'data-mpt-id': data.mpt_id,
          };
        }

        switch (data.size) {
          case 'Teaser-Size-100':
            slideProps.type = TEASER_TYPE.size100;
            break;
          case 'Teaser-Size-66':
            slideProps.type = TEASER_TYPE.size66;
            break;

          case 'Teaser-Size-50':
            slideProps.type = TEASER_TYPE.size50;
            break;

          case 'Teaser-Size-33':
            slideProps.type = TEASER_TYPE.size33;
            break;

          default:
            slideProps.type = TEASER_TYPE.banner;
            break;
        }

        if (hasSlider) {
          slideProps.type = TEASER_TYPE.banner;
        }

        return {
          ...slideProps,
          videoUrl: data.video_url,
          buttonLabel: data.cta_label,
          content: data.description,
          contentPosition: data.content_position,
          imagePosition: data.image_focus,
          //TODO: add fallback img
          imageUrl: data.image ? data.image.url : '',
          logoPosition: data.logo_position,
          logoUrl: data.logo && data.logo.url,
          onClick: handleClick,
          styleType: data.style_type,
          title: data.label,
          text,
          url: data.cta_url?.trim() || null,
        } as BannerSlideProps;
      },
    );

    const injectedProps: InjectedProps = {
      hasSlider,
      slidesProps,
    };

    return <Component {...originalProps} {...injectedProps} />;
  };

  return WithContainer;
};

export { withContainer };
